import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useMemo } from 'react';

import { GET_AUDIT_SESSION_STATS } from '../graphql/queries/getAuditSessionStats';

interface GetAuditSessionStatsResponse {
  auditSessionStats: {
    totalCount: number;
    completeCount: number;
    skippedCount: number;
    incompleteCount: number;
    canComplete: boolean;
  };
}

export interface AuditSessionStats {
  totalCount: number;
  completeCount: number;
  skippedCount: number;
  incompleteCount: number;
  canComplete: boolean;
  sampleStatistics?: {
    sampleSize: number;
    confidenceLevel: number;
    marginOfError: number;
    populationSize: number;
  };
  datasetComposition?: {
    byTopic: Array<{
      key: string;
      count: number;
      percentage: number;
    }>;
    byLanguage: Array<{
      key: string;
      count: number;
      percentage: number;
    }>;
    byLabel: Array<{
      key: string;
      count: number;
      percentage: number;
    }>;
    bySeverity: Array<{
      key: string;
      count: number;
      percentage: number;
    }>;
    totalItems: number;
  };
}

interface UseGetAuditSessionStatsOptions {
  skip?: boolean;
  pollInterval?: number;
}

/**
 * Hook for fetching audit session statistics and progress data
 * Part of the 4-query architecture - fetches only statistics and progress data
 */
export function useGetAuditSessionStats(sessionId: string | undefined, options: UseGetAuditSessionStatsOptions = {}) {
  const { skip = false, pollInterval } = options;

  const { data, loading, error, refetch } = useQuery<GetAuditSessionStatsResponse>(GET_AUDIT_SESSION_STATS, {
    variables: { auditSessionId: sessionId },
    skip: !sessionId || skip,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data on first load
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: false, // Don't show loading during background refetches
    pollInterval,
  });

  // Transform raw data to expected stats format
  const stats = useMemo((): AuditSessionStats | null => {
    if (!data?.auditSessionStats) return null;

    const sessionStats = data.auditSessionStats;

    return {
      totalCount: sessionStats.totalCount,
      completeCount: sessionStats.completeCount,
      skippedCount: sessionStats.skippedCount,
      incompleteCount: sessionStats.incompleteCount,
      canComplete: sessionStats.canComplete,
      // These fields are not available in the auditSessionStats query
      // Components should use separate queries for this data if needed
      sampleStatistics: undefined,
      datasetComposition: undefined,
    };
  }, [data]);

  // Utility functions
  const utils = useMemo(() => {
    if (!stats) return null;

    return {
      getCompletionPercentage: () => {
        if (stats.totalCount === 0) return 0;

        return Math.round(((stats.completeCount + stats.skippedCount) / stats.totalCount) * 100);
      },
      hasIncompleteItems: () => stats.incompleteCount > 0,
    };
  }, [stats]);

  // Transform error using adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    const mappedError = transformError(error);

    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  return {
    stats,
    loading,
    error: domainError,
    refetch,
    utils,
  };
}
